import React from "react";
import { Box } from "@mui/material";
import BioAgeAnalysisCard from "./BioAgeAnalysisCard";
import BioAgeCenterColumn from "./BioAgeCenterColumn";
import SystemsImpactCard from "./SystemsImpactCard";
import BioAgeProgressTimeline from "./BioAgeProgressTimeline";
import ConsultationHowToCard from "./ConsultationHowToCard";
import Reveal from "../shared/Reveal";
import { useConsultation } from "../../ConsultationContext";
import { isEmptyBioAgeMeasurement } from "../../data/fetchConsultationPrep";

/**
 * Section 1 · Bio Age — single layout for all four sub-steps. Every element is
 * rendered up front (so the card never resizes between sub-steps); advancing a
 * sub-step just fades the next element in:
 *
 *   sub 0 (clock)      → clock only
 *   sub 1 (comparison) → + comparison line
 *   sub 2 (younger)    → + "Making you younger" panel (left)
 *   sub 3 (older)      → + "Making you older" panel (right) + progress timeline
 *
 * When history exists, the timeline's space is reserved from the start (it's
 * rendered but transparent until sub-step 3), so the card keeps a constant
 * height across every sub-step. On sub-steps 0–2 the clock block is nudged
 * down to sit vertically centred within that reserved space; on sub-step 3 it
 * slides back up and the timeline fades in below it.
 *
 * In AI Guided mode the clock sub-step additionally shows the "How to use the
 * digital consultation" panel under the clock, so the user reads how to start
 * the call before pressing the phone button. It takes the timeline's slot in
 * the centre column (the clock is not nudged while it is shown) and, because
 * the column is narrow between the two reserved side panels, it is widened
 * with negative margins to span the whole row — the side panels are still
 * invisible on this sub-step, so nothing is covered. Its height is about the
 * timeline's, so the card stays within its 502px floor and the page never
 * has to scroll. On the next sub-step the panel unmounts and the timeline
 * reservation comes back. Never renders in self-guided mode.
 */
const BioAgeSection: React.FC = () => {
  const { data, stepper, aiActive } = useConsultation();
  const subIndex = stepper.current.subIndex; // 0 clock · 1 comparison · 2 younger · 3 older
  const showHowTo = aiActive && subIndex === 0;

  const history = data.prep?.bioAgeHistory ?? null;
  const hasHistory = history != null && !isEmptyBioAgeMeasurement(history);
  const showTimeline = hasHistory && subIndex >= 3;

  return (
    <BioAgeAnalysisCard>
      <Box sx={styles.row}>
        <Reveal visible={subIndex >= 2}>
          <SystemsImpactCard variant="younger" />
        </Reveal>

        <Box sx={styles.center}>
          <Box sx={styles.clockShift(hasHistory && !showTimeline && !showHowTo)}>
            <BioAgeCenterColumn
              showComparison
              comparisonVisible={subIndex >= 1}
            />
          </Box>
          {showHowTo ? (
            <Box sx={styles.howToSlot}>
              <ConsultationHowToCard />
            </Box>
          ) : (
            hasHistory && (
              <Reveal visible={showTimeline}>
                <BioAgeProgressTimeline />
              </Reveal>
            )
          )}
        </Box>

        <Reveal visible={subIndex >= 3}>
          <SystemsImpactCard variant="older" />
        </Reveal>
      </Box>
    </BioAgeAnalysisCard>
  );
};

export default BioAgeSection;

// Width of each `SystemsImpactCard` side panel (see its `cardYounger` /
// `cardOlder` styles) and the row gap below. The how-to panel spans the row by
// overflowing the centre column by exactly one panel + gap on each side.
const SIDE_PANEL_WIDTH_PX = 295;
const ROW_GAP_PX = 32;

const styles = {
  row: {
    display: "flex",
    gap: `${ROW_GAP_PX}px`,
    // Side panels top-align; the center column is the tallest element (it
    // reserves the timeline's space) so it defines the card height, and the
    // clock stays vertically centred within it via `clockShift`.
    alignItems: "flex-start",
    width: "100%",
    justifyContent: "center",
  },
  center: {
    flex: 1,
    minWidth: 0,
    display: "flex",
    flexDirection: "column" as const,
    alignItems: "center",
    // Reserve the gap to the timeline always (when present), so the column
    // height — and therefore the card — stays constant across sub-steps.
    gap: "24px",
  },
  // While the timeline's space is reserved but hidden (sub-steps 0–2 with
  // history), nudge the clock block down by half the reserved area
  // (gap 24 + timeline 144)/2 = 84px so it reads as vertically centred. On
  // the last sub-step it slides back to the top to sit above the timeline.
  clockShift: (shiftDown: boolean) => ({
    transform: shiftDown ? "translateY(84px)" : "translateY(0)",
    transition: "transform 400ms ease",
  }),
  // Lets the how-to panel span the full row while living in the centre
  // column (flex `align-items: center` keeps the overflow symmetric).
  howToSlot: {
    width: `calc(100% + ${2 * (SIDE_PANEL_WIDTH_PX + ROW_GAP_PX)}px)`,
    flexShrink: 0,
  },
};
